import {
  ActionIcon,
  Avatar,
  Badge,
  Group,
  Loader,
  NativeSelect,
  Pagination,
  Stack,
  Table,
  Text,
  VisuallyHidden,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, LogOut, Trash2 } from "lucide-react";
import { useState } from "react";

import { useListMemberAchievements } from "../../api/generated/endpoints/achievements/achievements";
import {
  getListInvitationsQueryKey,
  getListMembersQueryKey,
  useChangeMemberRole,
  useListInvitations,
  useListMembers,
  useRemoveMember,
  useResendInvitation,
  useRevokeInvitation,
} from "../../api/generated/endpoints/teams/teams";
import type { Invitation, Member } from "../../api/generated/model";
import { TeamRole } from "../../api/generated/zod";
import { refreshAccessScope } from "../../api/session";
import { ErrorAlert } from "../ErrorAlert";
import type { Membership } from "../membership";
import {
  ROLE_LABEL,
  assignableRoles,
  canManageMember,
  invitableRoles,
  managesMembers,
} from "../permissions";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { TableFrame } from "../../ui/TableFrame";
import { breakableEmail } from "./email";
import { MemberAchievements } from "./MemberAchievements";
import { PendingRow } from "./PendingRow";

/** The live app pages its members table by ten (design-system.md 3.7). */
const PAGE_SIZE = 10;

function displayName(member: Member): string {
  return member.name ?? member.email ?? "Unknown user";
}

/** A member, or a pending invitation for those who manage members (spec teams/invite-by-email). */
type Entry = { kind: "member"; member: Member } | { kind: "invitation"; invitation: Invitation };

/** Live's order: by date, oldest first, a member by joining and an invitation by sending. */
function dated(entry: Entry): number {
  return Date.parse(entry.kind === "member" ? entry.member.joined_at : entry.invitation.created_at);
}

function matches(entry: Entry, term: string): boolean {
  if (entry.kind === "invitation") return entry.invitation.email.toLowerCase().includes(term);
  const { member } = entry;
  return (
    (member.name ?? "").toLowerCase().includes(term) ||
    (member.email ?? "").toLowerCase().includes(term)
  );
}

export interface MemberListProps {
  teamId: string;
  me: Membership;
  /** Narrows the rows by name or email, case-insensitive; filtered in the browser. */
  search?: string;
  /** Says what was just done, on the page's status line ("Invitation resent"). */
  onNotice?: (notice: string | undefined) => void;
  /** The table scrolls sideways in its box on a narrow screen (the Pro Studio redesign). */
  scroll?: boolean | undefined;
}

export function MemberList({ teamId, me, search = "", onNotice, scroll }: MemberListProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const members = useListMembers(teamId);
  const manages = managesMembers(me.role);
  const invitations = useListInvitations(teamId, { query: { enabled: manages } });
  // Who shares their achievements here, and what (spec web/achievements, 5); nobody else shows any.
  const sharing = new Map((useListMemberAchievements(teamId).data ?? []).map((each) => [each.user_id, each]));
  const [revoking, setRevoking] = useState<Invitation | undefined>(undefined);
  const [removing, setRemoving] = useState<Member | undefined>(undefined);
  const [page, setPage] = useState(1);
  // After any change, the list and the scope are read again: the API is the source of truth,
  // so a refused change (stale hints) also brings the screen up to date.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getListMembersQueryKey(teamId) }),
      refreshAccessScope(queryClient),
    ]);
  const change = useChangeMemberRole({ mutation: { onSettled: refresh } });
  const remove = useRemoveMember({
    mutation: {
      onSuccess: async (_, { userId }) => {
        if (userId === me.userId) {
          // Left the team: nothing of it may stay on screen; `/` opens the next team.
          queryClient.clear();
          await navigate({ to: "/", replace: true });
          return;
        }
        setRemoving(undefined);
        await refresh();
      },
      onError: refresh,
    },
  });
  const refreshInvitations = () =>
    queryClient.invalidateQueries({ queryKey: getListInvitationsQueryKey(teamId) });
  const resend = useResendInvitation({
    mutation: {
      onMutate: () => onNotice?.(undefined),
      onSuccess: () => onNotice?.("Invitation resent"),
      onSettled: refreshInvitations,
    },
  });
  const revoke = useRevokeInvitation({
    mutation: {
      onSuccess: async () => {
        setRevoking(undefined);
        await refreshInvitations();
      },
    },
  });

  if (members.isPending || (manages && invitations.isPending)) {
    return <Loader size="sm" aria-label="Loading members" />;
  }
  if (members.isError) return <ErrorAlert error={members.error} />;

  // A query switched off keeps its last answer: rows only while this role manages members.
  const pending = manages && invitations.isSuccess ? invitations.data : [];
  const entries: Entry[] = [
    ...members.data.map((member) => ({ kind: "member" as const, member })),
    ...pending.map((invitation) => ({ kind: "invitation" as const, invitation })),
  ].sort((a, b) => dated(a) - dated(b));
  const term = search.trim().toLowerCase();
  const shown = term === "" ? entries : entries.filter((each) => matches(each, term));
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const first = (current - 1) * PAGE_SIZE;
  const rows = shown.slice(first, first + PAGE_SIZE);

  const roleCell = (member: Member) => {
    if (!canManageMember(me.role, member.role)) return ROLE_LABEL[member.role];
    return (
      <NativeSelect
        aria-label={`Role of ${displayName(member)}`}
        size="xs"
        w={110}
        // Live's role select shows a down chevron, not the native up-down arrows.
        rightSection={<ChevronDown size={14} aria-hidden />}
        disabled={change.isPending}
        data={assignableRoles(me.role).map((role) => ({ value: role, label: ROLE_LABEL[role] }))}
        value={member.role}
        onChange={(event) => {
          const role = TeamRole.safeParse(event.currentTarget.value);
          if (!role.success) return;
          change.mutate({ teamId, userId: member.user_id, data: { role: role.data } });
        }}
      />
    );
  };
  // Resend and revoke need the right to send that role (A2).
  const mine = invitableRoles(me.role);
  const action = (member: Member) => {
    const self = member.user_id === me.userId;
    if (self ? member.role === "owner" : !canManageMember(me.role, member.role)) return null;
    return (
      <ActionIcon
        variant="subtle"
        color="red"
        aria-label={self ? "Leave team" : `Remove ${displayName(member)}`}
        onClick={() => setRemoving(member)}
      >
        {self ? <LogOut size={16} /> : <Trash2 size={16} />}
      </ActionIcon>
    );
  };

  const footer =
    shown.length > PAGE_SIZE ? (
      <>
        <Text size="sm">Items per page: {PAGE_SIZE}</Text>
        <Text size="sm" component="output">
          Showing {first + 1} - {Math.min(first + PAGE_SIZE, shown.length)} of {shown.length}
        </Text>
        <Pagination total={pages} value={current} onChange={setPage} size="sm" />
      </>
    ) : undefined;

  return (
    <Stack gap="sm">
      {change.isError ? <ErrorAlert error={change.error} /> : null}
      {manages && invitations.isError ? <ErrorAlert error={invitations.error} /> : null}
      {resend.isError ? <ErrorAlert error={resend.error} /> : null}
      <TableFrame footer={footer} scroll={scroll}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>
              <VisuallyHidden>Actions</VisuallyHidden>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((entry) => {
            if (entry.kind === "invitation") {
              const { invitation } = entry;
              return (
                <PendingRow
                  key={invitation.id}
                  invitation={invitation}
                  canManage={mine.includes(invitation.role)}
                  resending={resend.isPending && resend.variables.invitationId === invitation.id}
                  onResend={() => resend.mutate({ teamId, invitationId: invitation.id })}
                  onRevoke={() => setRevoking(invitation)}
                />
              );
            }
            const { member } = entry;
            const shared = sharing.get(member.user_id);
            return (
              <Table.Tr key={member.user_id}>
                <Table.Td>
                  <Group gap="sm" wrap="nowrap">
                    {/* Decorative: the name beside it is the cell's text. */}
                    <Avatar
                      size={38}
                      radius={8}
                      // Live's rule: the member's picture, else initials in indigo on light
                      // indigo. Live's one-letter colored avatars are Google's pictures.
                      color="indigo"
                      name={displayName(member)}
                      src={member.picture_url}
                      aria-hidden
                    />
                    <Text size="sm" fw={600}>
                      {displayName(member)}{" "}
                      {member.user_id === me.userId ? <Badge size="xs">You</Badge> : null}
                    </Text>
                    {shared === undefined ? null : (
                      <MemberAchievements teamId={teamId} name={displayName(member)} shared={shared} />
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{breakableEmail(member.email ?? "")}</Text>
                </Table.Td>
                <Table.Td>{roleCell(member)}</Table.Td>
                <Table.Td align="right">{action(member)}</Table.Td>
              </Table.Tr>
            );
          })}
          {rows.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={4}>
                <Text size="sm" c="var(--cai-color-text-muted)">
                  No member matches &quot;{search.trim()}&quot;.
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : null}
        </Table.Tbody>
      </TableFrame>
      {me.role === "owner" ? (
        <Text size="sm" c="var(--cai-color-text-muted)">
          You own this team, so you cannot leave it or change your role. To hand it over, invite
          the new owner with the Owner role; when they accept, you become an admin.
        </Text>
      ) : null}
      {removing === undefined ? null : (
        <ConfirmModal
          opened
          title={
            removing.user_id === me.userId
              ? "Leave this team?"
              : `Remove ${displayName(removing)} from the team?`
          }
          confirmLabel={removing.user_id === me.userId ? "Leave team" : "Remove member"}
          onConfirm={() => remove.mutate({ teamId, userId: removing.user_id })}
          onCancel={() => {
            remove.reset();
            setRemoving(undefined);
          }}
          pending={remove.isPending}
          error={remove.isError ? <ErrorAlert error={remove.error} /> : null}
        >
          <Text size="sm">
            {removing.user_id === me.userId
              ? "You lose access to the team at once. An admin can invite you again."
              : "They lose access at once. What they made stays with the team."}
          </Text>
        </ConfirmModal>
      )}
      {revoking === undefined ? null : (
        <ConfirmModal
          opened
          title={`Revoke the invitation to ${revoking.email}?`}
          confirmLabel="Revoke invitation"
          onConfirm={() => revoke.mutate({ teamId, invitationId: revoking.id })}
          onCancel={() => {
            revoke.reset();
            setRevoking(undefined);
          }}
          pending={revoke.isPending}
          error={revoke.isError ? <ErrorAlert error={revoke.error} /> : null}
        >
          <Text size="sm">The link in their email stops working. You can invite them again.</Text>
        </ConfirmModal>
      )}
    </Stack>
  );
}
